import { z } from "zod";

/**
 * Shared DTO cho System Settings registry (US-AE2.F1-0005 / @UC9010) — một nguồn sự thật
 * cho api (S01 — guard + validate biên PATCH registry edit) và admin web (S04 — render/sửa).
 *
 * Khớp đúng `SystemSettingView` (system-settings.service.ts) và Prisma model SystemSetting:
 *  - key: string
 *  - defaultValue: unknown (Json — primitive/array/object)
 *  - allowed: unknown (Json — {enum:[...]} | {min,max})
 *  - groupOverridable: boolean
 *  - description: string | null
 *
 * BẢO MẬT / invariants:
 * - DTO chỉ mang config value — KHÔNG secret/token (C2 redaction là việc của audit diff).
 * - PATCH body `.strict()` (chặn key lạ) + `.refine` (ít nhất 1 field) — fail-closed tại biên.
 */

/** View read-only của một row registry (khớp SystemSettingView + Prisma SystemSetting). */
export const systemSettingSchema = z.object({
  key: z.string(),
  defaultValue: z.unknown(),
  allowed: z.unknown(),
  groupOverridable: z.boolean(),
  description: z.string().nullable(),
});

export type SystemSettingDto = z.infer<typeof systemSettingSchema>;

/**
 * Guardrail `allowed` mà admin SET qua PATCH (@UC9010) — khớp đúng hai dạng backend hỗ trợ
 * (api/.../allowed-value.ts):
 *  - { enum: [...] } — danh sách non-empty, cap 100 phần tử (chặn payload phình + DoS biên).
 *  - { min, max }    — khoảng số.
 * `.strict()` mỗi nhánh để loại field lạ (fail-closed input · C5). READ DTO vẫn permissive
 * (z.unknown) để không reject row cũ; chỉ thứ admin GHI mới bị siết.
 */
const ALLOWED_ENUM_MAX_ITEMS = 100;

const patchAllowedSchema = z.union([
  z
    .object({ enum: z.array(z.unknown()).min(1).max(ALLOWED_ENUM_MAX_ITEMS) })
    .strict(),
  z.object({ min: z.number(), max: z.number() }).strict(),
]);

export type PatchAllowedSpec = z.infer<typeof patchAllowedSchema>;

/**
 * Body của PATCH /v1/admin/system-settings/:key (registry edit @UC9010).
 *
 * Cả 4 field OPTIONAL nhưng `.refine` ép ít nhất một field hiện diện (chặn body rỗng → 400).
 * `value` union string(max512)|number|array<string> tuỳ guardrail `allowed`/khoá của row;
 * mảng string phục vụ `supported_locales` (US-AE2.F5-0007 · @UC9012). Ràng buộc value-vs-allowed
 * và quy tắc locale do service kiểm (422). `allowed` siết về union enum|range tại biên (C5).
 * `.strict()` để loại key ngoài contract (fail-closed input).
 */
const SUPPORTED_LOCALES_MAX_ITEMS = 20;

export const patchSystemSettingSchema = z
  .object({
    // string|number = giá trị config thường; boolean = US-AE2.F2-0006 (vd pay_on_behalf_creates_debt);
    // string[] = US-AE2.F5-0007 (vd supported_locales). guardrail value-vs-allowed + rule
    // money-policy/locale do service kiểm (422/409).
    value: z
      .union([
        z.string().max(512),
        z.number(),
        z.boolean(),
        z.array(z.string().max(64)).max(SUPPORTED_LOCALES_MAX_ITEMS),
      ])
      .optional(),
    allowed: patchAllowedSchema.optional(),
    groupOverridable: z.boolean().optional(),
    description: z.string().max(1024).nullable().optional(),
  })
  .strict()
  .refine(
    (body) =>
      body.value !== undefined ||
      body.allowed !== undefined ||
      body.groupOverridable !== undefined ||
      body.description !== undefined,
    { message: "At least one field is required" },
  );

export type PatchSystemSettingRequest = z.infer<typeof patchSystemSettingSchema>;
